import React, { useRef } from "react";
import { Alert, Card } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/esm/Col";
import Row from "react-bootstrap/esm/Row";
import Table from "react-bootstrap/Table";
import { faColumns, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingOverlay } from "../../common/Loading/LoadingOverlay";
import { ColumnRender, getColumnAlignClass, IColumn, IPagedListAction, IPagedListRowIndicator, SortOrder } from "../Declarations";
import { EllipsisActionMenu } from "../EllipsisActionMenu";
import { StickyScrollBar } from "../StickyScrollBar";
import { ValueRenderer } from "../ValueRenderer";
import { renderPagedListRowLeadingIcons } from "../../../utils";

interface PagedListTableProps {
    visibleColumns: IColumn[];
    results: any[];
    hasLoadError: boolean;
    loadErrorMessage: string | null;
    hasResults: boolean;
    total: number;
    isBusy: boolean;
    tableWidth: number;
    sortBy: string;
    sortOrder: SortOrder;
    primaryActions: IPagedListAction[];
    secondaryActions: IPagedListAction[];
    keyColumn: string;
    avatarUrl?: string;
    rowActionsAriaLabel?: string;
    onSortByClicked: (member: string) => void;
    onSelectColumnsClicked: () => void;
    showSelectColumns?: boolean;
    onScroll: (value: number) => void;
    getRowClassName: (item: any) => string;
    rowIndicators?: IPagedListRowIndicator[];
}

export const PagedListTable: React.FC<PagedListTableProps> = ({
    visibleColumns,
    results,
    hasLoadError,
    loadErrorMessage,
    hasResults,
    total,
    isBusy,
    tableWidth,
    sortBy,
    sortOrder,
    primaryActions,
    secondaryActions,
    keyColumn,
    avatarUrl,
    rowActionsAriaLabel = "Row actions",
    onSortByClicked,
    onSelectColumnsClicked,
    showSelectColumns = true,
    onScroll,
    getRowClassName,
    rowIndicators = [],
}) => {
    const pagedListTableContainer = useRef<HTMLDivElement | null>(null);

    const handleScroll = (value: number) => {
        if (pagedListTableContainer.current) {
            pagedListTableContainer.current.scrollLeft = value;
        }
        onScroll(value);
    };

    const hasRowActions = primaryActions.length > 0 || secondaryActions.length > 0;
    const hasTrailingColumn = showSelectColumns || hasRowActions;
    const columnCount = visibleColumns.length + (hasTrailingColumn ? 1 : 0);

    return (
        <Row className="my-3">
            <Col>
                <LoadingOverlay active={isBusy}>
                    <Card className="shadow">
                        <StickyScrollBar 
                            onScroll={handleScroll} 
                            scrollWidth={tableWidth} 
                            scrollLeft={pagedListTableContainer.current?.scrollLeft}
                        >
                            <div className="paged-list-table-container" ref={pagedListTableContainer}>
                                <Table hover size="sm" className="my-0" style={{ width: tableWidth }}>
                                    <thead className="bg-body border-bottom">
                                        <tr>
                                            {visibleColumns.map((column, index) => {
                                                const colAlign = getColumnAlignClass(column);
                                                const headerJustify =
                                                    colAlign === "text-end"
                                                        ? "justify-content-end"
                                                        : colAlign === "text-center"
                                                          ? "justify-content-center"
                                                          : "justify-content-start";
                                                return (
                                                <React.Fragment key={`${column.member}-${index}`}>
                                                    <th className={`p-0 ${colAlign}`} style={{ width: `${column.width}px` }}>
                                                        {column.canSort !== false && (
                                                            <Button
                                                                variant="outline-th"
                                                                className={`w-100 border-0 rounded-0 m-0 p-2 text-dark d-flex align-items-center ${headerJustify} ${colAlign}`}
                                                                onClick={() => onSortByClicked(column.member)}
                                                                title={column.renderAs === ColumnRender.deleted ? column.title : undefined}
                                                            >
                                                                {column.renderAs !== ColumnRender.deleted ? column.title : "\u00a0"}
                                                                {sortBy === column.member && sortOrder === SortOrder.ascending && (
                                                                    <FontAwesomeIcon className="ps-2 text-primary" icon={faSortUp} />
                                                                )}
                                                                {sortBy === column.member && sortOrder === SortOrder.descending && (
                                                                    <FontAwesomeIcon className="ps-2 text-primary" icon={faSortDown} />
                                                                )}
                                                            </Button>
                                                        )}
                                                        {column.canSort === false && (
                                                            <span className={`p-2 d-inline-block w-100 ${colAlign}`} title={column.renderAs === ColumnRender.deleted ? column.title : undefined}>
                                                                {column.renderAs !== ColumnRender.deleted ? column.title : "\u00a0"}
                                                            </span>
                                                        )}
                                                    </th>
                                                </React.Fragment>
                                                );
                                            })}
                                            {hasTrailingColumn && (
                                                <th className="text-end p-0 align-content-center paged-list-trailing-column">
                                                    {showSelectColumns && (
                                                        <Button
                                                            variant="outline-dark"
                                                            className="border-0 rounded-0 m-0 px-1 text-dark"
                                                            onClick={onSelectColumnsClicked}
                                                            title="Select columns"
                                                        >
                                                            <FontAwesomeIcon icon={faColumns} className="px-2 height-1em" />
                                                        </Button>
                                                    )}
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!hasLoadError && results !== undefined &&
                                            results.map((item) => (
                                                <tr key={JSON.stringify(item)} className={getRowClassName(item)}>
                                                    {visibleColumns.map((column, index) => (
                                                        <React.Fragment key={`${column.member}-${index}`}>
                                                            <ValueRenderer
                                                                item={item}
                                                                column={column}
                                                                avatarUrl={avatarUrl}
                                                                leadingIcons={
                                                                    index === 0
                                                                        ? renderPagedListRowLeadingIcons(item, rowIndicators)
                                                                        : undefined
                                                                }
                                                            />
                                                        </React.Fragment>
                                                    ))}
                                                    {hasTrailingColumn && (
                                                    <td className={`text-end px-2 row-actions-column ${getRowClassName(item)}`}>
                                                        {hasRowActions && (
                                                            <div className="d-inline-flex align-items-center justify-content-end gap-1">
                                                                {primaryActions.map((action) => (
                                                                    <Button
                                                                        key={action.text}
                                                                        onClick={() => action.onClick(item)}
                                                                        className={`mr-10 ${action.secondaryBtn ? "btn-secondary" : "btn-primary"}`}
                                                                    >
                                                                        {action.icon && (
                                                                            <FontAwesomeIcon fixedWidth icon={action.icon} className="me-2" />
                                                                        )}
                                                                        {action.text}
                                                                    </Button>
                                                                ))}
                                                                {secondaryActions.length > 0 && (
                                                                    <EllipsisActionMenu
                                                                        id={`row-actions-${item[keyColumn]}`}
                                                                        ariaLabel={rowActionsAriaLabel}
                                                                        actions={secondaryActions}
                                                                        item={item}
                                                                        usePortal
                                                                        className="position-static"
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    )}
                                                </tr>
                                            ))}
                                        {!hasLoadError && hasResults && total === 0 && (
                                            <tr>
                                                <td colSpan={columnCount} className="text-center">
                                                    No results to show
                                                </td>
                                            </tr>
                                        )}
                                        {!hasLoadError && !hasResults && (
                                            <tr>
                                                <td colSpan={columnCount} className="height-6em"></td>
                                            </tr>
                                        )}
                                        {hasLoadError && (
                                            <tr>
                                                <td colSpan={columnCount} className="text-center">
                                                    <Alert variant="danger">
                                                        <span className="fw-bold">Could not load results.</span> {`${loadErrorMessage}`}
                                                    </Alert>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </StickyScrollBar>
                    </Card>
                </LoadingOverlay>
            </Col>
        </Row>
    );
};
